import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle,
  CloudOff,
  CloudRain,
  CloudSnow,
  Droplets,
  MapPin,
  RefreshCw,
  Wind,
} from "lucide-react";
import {
  getDailyForecastForLocation,
  listLocations,
} from "@/lib/weather.functions";
import {
  getUpcomingCalendarEvents,
  type UpcomingEvent,
} from "@/lib/google-calendar.functions";
import { WeatherIcon, type WeatherSeverity } from "@/components/calendar/WeatherIcon";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : undefined;
}

const AVATAR_PALETTE = [
  "bg-[#9fe870] text-[#054d28]",
  "bg-[#fdba74] text-[#7c2d12]",
  "bg-[#fcd34d] text-[#78350f]",
  "bg-[#7dd3fc] text-[#075985]",
  "bg-[#f0abfc] text-[#701a75]",
  "bg-[#a5b4fc] text-[#312e81]",
  "bg-[#fda4af] text-[#881337]",
];

function initialsOf(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function isoDay(d: Date) {
  return d.toISOString().slice(0, 10);
}

function dayLabels(iso: string): {
  day: number;
  weekdayShort: string;
  weekdayLong: string;
  isToday: boolean;
} {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return {
    day: d,
    weekdayShort: date.toLocaleDateString(undefined, { weekday: "short" }),
    weekdayLong: date.toLocaleDateString(undefined, { weekday: "long" }),
    isToday: iso === isoDay(new Date()),
  };
}

const SEVERITY_BORDER: Record<WeatherSeverity, string> = {
  clear: "border-transparent",
  watch: "border-[#9fe870]/60",
  warn: "border-[#b86700]/60",
  severe: "border-[#a72027]/70",
};

type DayRow = {
  date: string;
  weather_code: number | null;
  temp_max_f: number | null;
  temp_min_f: number | null;
  precip_prob: number | null;
  precip_mm: number | null;
  snow_cm: number | null;
  wind_mph: number | null;
  wind_gust_mph: number | null;
  severity: WeatherSeverity;
  flag_reason: string | null;
  severe_alert: { event?: string; headline?: string } | null;
};

function precipKind(d: DayRow): "snow" | "rain" | "dry" {
  if ((d.snow_cm ?? 0) > 0) return "snow";
  if ((d.precip_prob ?? 0) >= 30 || (d.precip_mm ?? 0) > 0.2) return "rain";
  return "dry";
}

function headlineFor(d: DayRow): string {
  const kind = precipKind(d);
  if (kind === "snow") {
    const cm = Math.round((d.snow_cm ?? 0) * 10) / 10;
    return `Snow expected — about ${cm} cm`;
  }
  if (kind === "rain") {
    const p = Math.round(d.precip_prob ?? 0);
    const mm = Math.round((d.precip_mm ?? 0) * 10) / 10;
    return mm > 0
      ? `Rain likely — ${p}% chance, ${mm} mm`
      : `Showers possible — ${p}% chance`;
  }
  if ((d.wind_mph ?? 0) >= 15) {
    return `Dry but breezy — wind ${Math.round(d.wind_mph ?? 0)} mph`;
  }
  return "Dry and clear";
}

function WeatherPage() {
  const fetchDaily = useServerFn(getDailyForecastForLocation);
  const fetchLocs = useServerFn(listLocations);
  const fetchEvents = useServerFn(getUpcomingCalendarEvents);
  const qc = useQueryClient();

  const [locationId, setLocationId] = useState<string | null>(null);
  const [selectedISO, setSelectedISO] = useState<string | null>(null);

  const locsQ = useQuery({
    queryKey: ["coach-locations"],
    queryFn: async () => {
      const headers = await authHeaders();
      return (await fetchLocs({ headers })) as Array<{
        id: string;
        name: string;
        indoor: boolean;
        is_default: boolean;
        latitude: number | null;
      }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const outdoorLocs = useMemo(
    () => (locsQ.data ?? []).filter((l) => !l.indoor),
    [locsQ.data],
  );

  useEffect(() => {
    if (locationId) return;
    if (outdoorLocs.length === 0) return;
    const fallback =
      outdoorLocs.find((l) => l.is_default) ?? outdoorLocs[0] ?? null;
    if (fallback) setLocationId(fallback.id);
  }, [outdoorLocs, locationId]);

  const dailyQ = useQuery({
    queryKey: ["weather-daily", locationId, 10],
    enabled: !!locationId,
    queryFn: async () => {
      const headers = await authHeaders();
      return (await fetchDaily({
        data: { locationId: locationId!, days: 10 },
        headers,
      })) as Awaited<ReturnType<typeof getDailyForecastForLocation>>;
    },
    staleTime: 10 * 60 * 1000,
  });

  const eventsQ = useQuery({
    queryKey: ["calendar-events", 28],
    queryFn: async () => {
      const headers = await authHeaders();
      return (await fetchEvents({ data: { days: 28 }, headers })) as
        | { connected: true; events: UpcomingEvent[]; error?: string }
        | { connected: false; error: string };
    },
    staleTime: 5 * 60 * 1000,
  });

  const eventsByDay = useMemo(() => {
    const m = new Map<string, UpcomingEvent[]>();
    if (!eventsQ.data?.connected) return m;
    for (const e of eventsQ.data.events) {
      if (locationId && e.locationId !== locationId) continue;
      const iso = e.start.slice(0, 10);
      const arr = m.get(iso) ?? [];
      arr.push(e);
      m.set(iso, arr);
    }
    return m;
  }, [eventsQ.data, locationId]);

  // Pick today (or first day) once data loads
  useEffect(() => {
    if (selectedISO) return;
    const days = dailyQ.data?.days as DayRow[] | undefined;
    if (!days || days.length === 0) return;
    const today = isoDay(new Date());
    setSelectedISO(days.find((d) => d.date === today)?.date ?? days[0].date);
  }, [dailyQ.data, selectedISO]);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["weather-daily"] });
    qc.invalidateQueries({ queryKey: ["calendar-events", 28] });
    toast.success("Refreshing forecast…");
  }

  const noLocations = (locsQ.data?.length ?? 0) === 0;
  const onlyIndoor = !noLocations && outdoorLocs.length === 0;

  const daily = dailyQ.data;
  const days = (daily?.days ?? []) as DayRow[];
  const severeAlert =
    daily && daily.alerts.length > 0
      ? daily.alerts.find(
          (a) =>
            a.severity === "Extreme" ||
            a.severity === "Severe" ||
            /tornado|hurricane|severe thunderstorm|blizzard|ice storm/i.test(a.event),
        ) ?? daily.alerts[0]
      : null;

  const venueName = daily?.location?.name;
  const leadLine = venueName
    ? `Your 10-day outlook for upcoming lessons at ${venueName}.`
    : "Your 10-day outlook for upcoming lessons.";

  const selectedDay = days.find((d) => d.date === selectedISO) ?? days[0] ?? null;

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Weather
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
              10-day outlook
            </h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              {leadLine}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {outdoorLocs.length > 1 && (
              <Select
                value={locationId ?? undefined}
                onValueChange={(v) => {
                  setLocationId(v);
                  setSelectedISO(null);
                }}
              >
                <SelectTrigger className="h-9 rounded-[10px] bg-card text-sm">
                  <SelectValue placeholder="Pick a venue" />
                </SelectTrigger>
                <SelectContent>
                  {outdoorLocs.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      <span className="inline-flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {l.name}
                        {l.is_default && (
                          <span className="text-[10px] font-semibold text-muted-foreground">
                            default
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              variant="outline"
              onClick={refresh}
              className="rounded-[10px]"
              disabled={dailyQ.isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${dailyQ.isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {noLocations ? (
          <EmptyState
            title="Add a location to see forecasts"
            body="Save the courts, parks, or fields where you teach. We'll pull weather automatically for any outdoor venue."
            actionLabel="Add a venue"
            actionTo="/app/locations"
          />
        ) : onlyIndoor ? (
          <EmptyState
            title="You only have indoor venues"
            body="Indoor sessions don't track day-to-day weather. Add an outdoor venue if you want an outlook here."
            actionLabel="Add an outdoor venue"
            actionTo="/app/locations"
          />
        ) : !locationId || dailyQ.isLoading ? (
          <SkeletonRow />
        ) : !daily ? (
          <EmptyState
            title="Couldn't load forecast"
            body="We couldn't reach the forecast service or the venue isn't geocoded yet. Try refreshing — or re-save the venue with a valid zip."
            actionLabel="Open Locations"
            actionTo="/app/locations"
          />
        ) : daily.error === "no_coords" ? (
          <EmptyState
            title="This venue isn't geocoded yet"
            body="Re-save the venue with a valid zip and we'll fetch its coordinates."
            actionLabel="Open Locations"
            actionTo="/app/locations"
          />
        ) : daily.error ? (
          <EmptyState
            title="Forecast unavailable"
            body="The forecast service didn't return data for this venue. Try refreshing in a few minutes."
            icon={<CloudOff className="h-5 w-5" />}
          />
        ) : (
          <>
            {severeAlert && (
              <div className="mb-4 flex items-start gap-3 rounded-[16px] border border-[#a72027]/40 bg-[#fdebec] p-4 dark:bg-[#a72027]/15">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#a72027] text-white">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#a72027] dark:text-[#f87171]">
                    {severeAlert.event}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground/80">{severeAlert.headline}</p>
                </div>
              </div>
            )}

            {/* Day strip */}
            <div className="rounded-[16px] border bg-card p-3 md:p-4">
              <div className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory md:gap-3">
                {days.map((d) => (
                  <DayColumn
                    key={d.date}
                    day={d}
                    lessonsCount={(eventsByDay.get(d.date) ?? []).length}
                    selected={d.date === selectedISO}
                    onSelect={() => setSelectedISO(d.date)}
                  />
                ))}
              </div>
            </div>

            {/* Selected day detail */}
            {selectedDay && (
              <DayDetail
                day={selectedDay}
                lessons={eventsByDay.get(selectedDay.date) ?? []}
              />
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function DayColumn({
  day,
  lessonsCount,
  selected,
  onSelect,
}: {
  day: DayRow;
  lessonsCount: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const labels = dayLabels(day.date);
  const kind = precipKind(day);
  const precipPct = Math.round(day.precip_prob ?? 0);

  const precipBlock =
    kind === "rain" ? (
      <div className="flex flex-col items-center justify-center rounded-[12px] bg-[#7dd3fc]/15 px-2 py-3 text-[#075985] dark:text-[#7dd3fc]">
        <CloudRain className="h-6 w-6" />
        <p className="mt-1 text-base font-black leading-none">{precipPct}%</p>
      </div>
    ) : kind === "snow" ? (
      <div className="flex flex-col items-center justify-center rounded-[12px] bg-[#a5b4fc]/15 px-2 py-3 text-[#312e81] dark:text-[#a5b4fc]">
        <CloudSnow className="h-6 w-6" />
        <p className="mt-1 text-[11px] font-bold leading-none">Snow</p>
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center rounded-[12px] bg-muted/50 px-2 py-3 text-muted-foreground">
        <WeatherIcon
          weatherCode={day.weather_code}
          severity="clear"
          tempF={day.temp_max_f}
          precipProb={day.precip_prob}
          windMph={day.wind_mph}
          reason={null}
        />
        <p className="mt-1 text-[11px] font-semibold leading-none">Dry</p>
      </div>
    );

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group snap-start flex min-w-[88px] flex-1 flex-col items-center gap-2 rounded-[14px] border-l-4 px-2 py-3 text-center transition-colors ${
        SEVERITY_BORDER[day.severity]
      } ${selected ? "bg-muted/70" : "hover:bg-muted/40"}`}
    >
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {labels.weekdayShort}
        </p>
        <p className="font-display text-xl font-black leading-none">{labels.day}</p>
        {labels.isToday && (
          <span className="mt-1 inline-flex items-center rounded-full bg-[#2ead4b]/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#2ead4b]">
            Today
          </span>
        )}
      </div>
      {precipBlock}
      <div className="text-xs">
        <span className="font-bold text-foreground">
          {day.temp_max_f != null ? `${Math.round(day.temp_max_f)}°` : "--"}
        </span>{" "}
        <span className="text-muted-foreground">
          {day.temp_min_f != null ? `${Math.round(day.temp_min_f)}°` : ""}
        </span>
      </div>
      {(day.wind_mph ?? 0) >= 10 && (
        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
          <Wind className="h-3 w-3" />
          {Math.round(day.wind_mph ?? 0)}
        </span>
      )}
      {lessonsCount > 0 ? (
        <span className="inline-flex items-center rounded-full bg-foreground/5 px-2 py-0.5 text-[10px] font-semibold text-foreground">
          {lessonsCount} lesson{lessonsCount === 1 ? "" : "s"}
        </span>
      ) : (
        <span className="text-[10px] italic text-muted-foreground/60">—</span>
      )}
    </button>
  );
}

function DayDetail({ day, lessons }: { day: DayRow; lessons: UpcomingEvent[] }) {
  const labels = dayLabels(day.date);
  return (
    <div className="mt-4 grid gap-4 rounded-[16px] border bg-card p-5 md:grid-cols-[1.2fr_1fr]">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {labels.weekdayLong} ·{" "}
          {new Date(day.date + "T00:00:00").toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          })}
        </p>
        <h2 className="mt-1 font-display text-2xl font-black tracking-tight text-foreground md:text-3xl">
          {headlineFor(day)}
        </h2>
        {day.flag_reason && (
          <p className="mt-2 text-sm text-foreground/80">{day.flag_reason}</p>
        )}
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <Stat label="High" value={day.temp_max_f != null ? `${Math.round(day.temp_max_f)}°` : "--"} />
          <Stat label="Low" value={day.temp_min_f != null ? `${Math.round(day.temp_min_f)}°` : "--"} />
          <Stat
            label="Precip"
            value={`${Math.round(day.precip_prob ?? 0)}%${
              (day.precip_mm ?? 0) > 0 ? ` · ${Math.round((day.precip_mm ?? 0) * 10) / 10}mm` : ""
            }`}
            icon={<Droplets className="h-3.5 w-3.5" />}
          />
          {(day.snow_cm ?? 0) > 0 && (
            <Stat
              label="Snow"
              value={`${Math.round((day.snow_cm ?? 0) * 10) / 10} cm`}
              icon={<CloudSnow className="h-3.5 w-3.5" />}
            />
          )}
          <Stat
            label="Wind"
            value={`${Math.round(day.wind_mph ?? 0)} mph${
              (day.wind_gust_mph ?? 0) > (day.wind_mph ?? 0) + 5
                ? ` · gust ${Math.round(day.wind_gust_mph ?? 0)}`
                : ""
            }`}
            icon={<Wind className="h-3.5 w-3.5" />}
          />
        </div>
      </div>
      <div className="border-t pt-4 md:border-l md:border-t-0 md:pl-5 md:pt-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Lessons scheduled
        </p>
        {lessons.length === 0 ? (
          <p className="mt-2 text-sm italic text-muted-foreground">
            Nothing on the books at this venue.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {lessons.map((e) => {
              const name =
                e.matchedClient?.name ?? e.attendee?.name ?? e.title ?? "Lesson";
              const time = new Date(e.start).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              });
              return (
                <li key={e.id} className="flex items-center gap-2 text-sm">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className={`text-[10px] font-bold ${paletteFor(name)}`}>
                      {initialsOf(name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-foreground">{name}</span>
                  <span className="text-xs text-muted-foreground">{time}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 inline-flex items-center gap-1 font-bold text-foreground">
        {icon}
        {value}
      </p>
    </div>
  );
}

function EmptyState({
  title,
  body,
  actionLabel,
  actionTo,
  icon,
}: {
  title: string;
  body: string;
  actionLabel?: string;
  actionTo?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-[16px] border border-dashed bg-card p-12 text-center">
      {icon && (
        <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {icon}
        </span>
      )}
      <p className="text-sm font-bold">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">{body}</p>
      {actionLabel && actionTo && (
        <Link to={actionTo}>
          <Button className="mt-4 rounded-[10px]">{actionLabel}</Button>
        </Link>
      )}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="rounded-[16px] border bg-card p-4">
      <div className="flex gap-3 overflow-hidden">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex min-w-[88px] flex-1 flex-col items-center gap-2">
            <Skeleton className="h-4 w-10" />
            <Skeleton className="h-16 w-full rounded-[12px]" />
            <Skeleton className="h-3 w-12" />
          </div>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/weather")({
  head: () => ({
    meta: buildAppMeta({
      title: "Weather — CoachQuick",
      description: "10-day outdoor coaching forecast for your upcoming lessons.",
      path: "/app/weather",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/weather` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <WeatherPage />
    </RequireAuth>
  ),
});
